import type { Theme } from '@epilot/journey-elements'
import { createStyles, makeStyles, Typography } from '@epilot/journey-elements'
import type { Price } from '@epilot/journey-logic-commons'
import {
  getPricingDetailsFormatted,
  hasPricingModel,
  hasTieredPricingModel
} from '@epilot/journey-logic-commons'
import { computeQuantities, PricingModel } from '@epilot/pricing'

import { getFormattedQuantity } from '../../../../../../utils/products/quantity-utils'
import { useTileContext } from '../../../utils/tile-context-utils'

import { PriceTierDetails } from './PriceTierDetails'

interface PriceItemComponentProps {
  priceComponent: Price
}

const useStyles = makeStyles<Theme>((theme) =>
  createStyles({
    priceComponent: {
      color: theme.palette.text.secondary,
      fontSize: 12
    }
  })
)

export const PriceItemComponent = ({
  priceComponent
}: PriceItemComponentProps) => {
  const classes = useStyles()
  const { actions, config, tileState } = useTileContext()
  const { quantityToSelectTier } = computeQuantities(
    priceComponent,
    tileState.quantity,
    {
      frequency_unit: priceComponent._price.blockMappingData?.frequencyUnit,
      value: priceComponent._price.blockMappingData?.numberInput
    }
  )

  const { unitAmount, unitAmountWithUnit, unit, billingPeriod } =
    getPricingDetailsFormatted(
      priceComponent,
      1,
      quantityToSelectTier,
      actions.t,
      Boolean(config.generalTileConfiguration.showTrailingDecimalZeros)
    )

  const formattedQuantity = getFormattedQuantity(
    priceComponent._price as Price,
    actions.t
  )

  return (
    <Typography
      className={classes.priceComponent}
      data-testid="price-item-amount"
    >
      {hasPricingModel(PricingModel.perUnit, priceComponent) && (
        <>
          <b>{unitAmountWithUnit}</b>
          {' - '}
          {priceComponent.description}
        </>
      )}
      {hasPricingModel(PricingModel.externalGetAG, priceComponent) && (
        <>
          <b>{unitAmountWithUnit}</b>
          {billingPeriod && priceComponent.get_ag?.type === 'base_price'
            ? ` ${billingPeriod.toLocaleLowerCase()} - `
            : ' - '}
          {priceComponent.description}
        </>
      )}
      {hasTieredPricingModel(priceComponent) && (
        <PriceTierDetails
          billingPeriod={billingPeriod}
          formattedQuantity={formattedQuantity}
          priceItem={priceComponent}
          unit={unit}
          unitAmount={unitAmount}
          unitAmountWithUnit={unitAmountWithUnit}
        />
      )}
    </Typography>
  )
}
